/**
 * Corpus Reader data hooks (Task 189). Fetch the static manifest served from
 * /corpus-data/ (the app's public/ dir; Task 188 output mirrored there). The
 * index manifest loads once for navigation; a chapter's content lazy-loads on
 * demand. No RAG/Qdrant calls at runtime (Task 187).
 *
 * Data lives under /corpus-data/ (NOT /corpus/, which is the SPA route) so the
 * extensionful JSON is served statically while extensionless /corpus/* routes
 * hit the SPA fallback.
 */
import { useEffect, useState } from "react";
import type {
  CorpusChapterContent,
  CorpusManifest,
  CorpusShortContent,
  LatestBook,
} from "./corpus-types";

const DATA_BASE = "/corpus-data";

type Load<T> = { state: "loading" } | { state: "ready"; data: T } | { state: "error" } | { state: "empty" };

export function useCorpusManifest(): Load<CorpusManifest> {
  const [result, setResult] = useState<Load<CorpusManifest>>({ state: "loading" });
  useEffect(() => {
    // The fetch may settle after navigation/unmount; suppress that stale state write.
    let alive = true;
    // `cache: "no-cache"` forces revalidation (conditional GET → 304 when
    // unchanged, fresh 200 after a corpus deploy). Without it, browsers apply
    // heuristic caching to these no-Cache-Control static files and a newly
    // published book stays invisible until the cache expires.
    fetch(`${DATA_BASE}/manifest.json`, { headers: { Accept: "application/json" }, cache: "no-cache" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: CorpusManifest) => {
        if (!alive) return;
        if (!data || !Array.isArray(data.books) || data.books.length === 0) setResult({ state: "empty" });
        else setResult({ state: "ready", data });
      })
      .catch(() => alive && setResult({ state: "error" }));
    return () => {
      alive = false;
    };
  }, []);
  return result;
}

export function useCorpusChapter(book: string | null, chapter: number | null): Load<CorpusChapterContent> {
  const [result, setResult] = useState<Load<CorpusChapterContent>>({ state: "loading" });
  useEffect(() => {
    if (!book || typeof chapter !== "number") {
      setResult({ state: "empty" });
      return;
    }
    // A fast chapter change can let the older request finish last; ignore it.
    let alive = true;
    setResult({ state: "loading" });
    fetch(`${DATA_BASE}/${encodeURIComponent(book)}/${chapter}.json`, { headers: { Accept: "application/json" }, cache: "no-cache" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: CorpusChapterContent) => {
        if (!alive) return;
        if (!data || !Array.isArray(data.chunks) || data.chunks.length === 0) setResult({ state: "empty" });
        else setResult({ state: "ready", data });
      })
      .catch(() => alive && setResult({ state: "error" }));
    return () => {
      alive = false;
    };
  }, [book, chapter]);
  return result;
}

/** Lazy-load one canonical FAQ entry. The video id is the route/storage key. */
export function useCorpusShort(videoId: string | null): Load<CorpusShortContent> {
  const [result, setResult] = useState<Load<CorpusShortContent>>({ state: "loading" });
  useEffect(() => {
    if (!videoId || !/^[A-Za-z0-9_-]{11}$/.test(videoId)) {
      setResult({ state: "empty" });
      return;
    }
    let alive = true;
    setResult({ state: "loading" });
    fetch(`${DATA_BASE}/shorts/${encodeURIComponent(videoId)}.json`, {
      headers: { Accept: "application/json" },
      cache: "no-cache",
    })
      .then((response) =>
        response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`)),
      )
      .then((data: CorpusShortContent) => {
        if (!alive) return;
        if (
          data?.content_kind !== "short" ||
          data.video_id !== videoId ||
          !Array.isArray(data.chunks) ||
          data.chunks.length === 0
        ) {
          setResult({ state: "empty" });
        } else {
          setResult({ state: "ready", data });
        }
      })
      .catch(() => alive && setResult({ state: "error" }));
    return () => {
      alive = false;
    };
  }, [videoId]);
  return result;
}

/**
 * corpus-data/latest.json — the most recently published book.
 *
 * The artifact has been generated by `corpus:reader` since the reader shipped
 * ("the newest book, for the home 'library is growing' notice") but nothing ever
 * consumed it, so a new book arrived silently. The library is the point of the
 * product; when it grows, say so.
 *
 * Fails SILENTLY: a missing/!ok/malformed latest.json yields null and the notice
 * simply does not render. It must never be able to break /corpus.
 */
export function useLatestBook(): LatestBook | null {
  const [latest, setLatest] = useState<LatestBook | null>(null);
  useEffect(() => {
    let alive = true;
    fetch(`${DATA_BASE}/latest.json`, { headers: { Accept: "application/json" }, cache: "no-cache" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: { book?: LatestBook | null }) => {
        if (!alive) return;
        const b = data?.book;
        if (b && typeof b.slug === "string" && typeof b.name === "string" && typeof b.chapters === "number") setLatest(b);
      })
      .catch(() => { /* notice is optional — never surface an error for it */ });
    return () => { alive = false; };
  }, []);
  return latest;
}
